import { lazy, Suspense } from 'react';
import type { ComponentType, LazyExoticComponent } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserRole } from '@myscaw/shared';
import { QueryProvider } from './shared/query/QueryProvider';
import { AuthProvider, useAuth } from './shared/auth/AuthContext';
import { RequireRole } from './shared/auth/RequireRole';
import { IfPermitted, RequirePermission } from './shared/auth/RequirePermission';
import { ToastProvider } from './shared/ui/Toast';
import { AppShell } from './shared/layout/AppShell';
import { RouteFallback } from './shared/ui/RouteFallback';
import { LandingPage } from './features/landing/LandingPage';
import { LoginPage } from './features/access/LoginPage';
import { SignUpPage } from './features/access/SignUpPage';
import { VerifyPage } from './features/access/VerifyPage';
import { ForgotPasswordPage } from './features/access/ForgotPasswordPage';
import { PrivacyPage, TermsPage } from './features/legal/LegalLayout';
import { NotFoundPage } from './features/legal/NotFoundPage';
import { CookieBanner } from './shared/CookieBanner';
import { InstallAppBanner } from './shared/ui/InstallAppBanner';
import { HOME } from './shared/layout/navigation';

/*
 * Route-level code-splitting (P3). Every signed-in feature page ships in its
 * own chunk, fetched on first navigation to it; the public shell - landing,
 * auth, legal docs, AppShell and the tab bar - stays eager in the critical
 * path. `lazyPage` maps a module's NAMED export onto React's `default`, so a
 * page component is never refactored for the sake of splitting.
 */
function lazyPage<T extends Record<string, ComponentType>>(
  importFn: () => Promise<T>,
  exportName: keyof T,
): LazyExoticComponent<ComponentType> {
  return lazy(() => importFn().then((mod) => ({ default: mod[exportName] })));
}

const ProfilePage = lazyPage(() => import('./features/access/ProfilePage'), 'ProfilePage');
const AnnouncementsPage = lazyPage(
  () => import('./features/announcement/student/AnnouncementsPage'),
  'AnnouncementsPage',
);
const AnnouncementDetailPage = lazyPage(
  () => import('./features/announcement/student/AnnouncementDetailPage'),
  'AnnouncementDetailPage',
);
const CreateAnnouncementPage = lazyPage(
  () => import('./features/announcement/admin/CreateAnnouncementPage'),
  'CreateAnnouncementPage',
);
const EditAnnouncementPage = lazyPage(
  () => import('./features/announcement/admin/EditAnnouncementPage'),
  'EditAnnouncementPage',
);
const ComplaintsPage = lazyPage(
  () => import('./features/complaint/student/ComplaintsPage'),
  'ComplaintsPage',
);
const ComplaintDetailPage = lazyPage(
  () => import('./features/complaint/student/ComplaintDetailPage'),
  'ComplaintDetailPage',
);
const NewComplaintPage = lazyPage(
  () => import('./features/complaint/student/NewComplaintPage'),
  'NewComplaintPage',
);
const AdminUsersPage = lazyPage(() => import('./features/admin/AdminUsersPage'), 'AdminUsersPage');

const WeekMenuPage = lazyPage(
  () => import('./features/restaurant/student/WeekMenuPage'),
  'WeekMenuPage',
);
const MealDetailPage = lazyPage(
  () => import('./features/restaurant/student/MealDetailPage'),
  'MealDetailPage',
);
const CheckInPage = lazyPage(
  () => import('./features/restaurant/student/CheckInPage'),
  'CheckInPage',
);
const DishDetailPage = lazyPage(
  () => import('./features/restaurant/student/DishHistoryPage'),
  'DishDetailPage',
);
const DishHistoryPage = lazyPage(
  () => import('./features/restaurant/student/DishHistoryPage'),
  'DishHistoryPage',
);
const MenusPage = lazyPage(() => import('./features/restaurant/manager/MenusPage'), 'MenusPage');
const MenuBuilderPage = lazyPage(
  () => import('./features/restaurant/manager/MenuBuilderPage'),
  'MenuBuilderPage',
);
const MealServicePage = lazyPage(
  () => import('./features/restaurant/manager/MealServicePage'),
  'MealServicePage',
);
const MealPhotosPage = lazyPage(
  () => import('./features/restaurant/manager/MealPhotosPage'),
  'MealPhotosPage',
);

const LibraryPage = lazyPage(() => import('./features/library/pages/LibraryPage'), 'LibraryPage');
const DocumentPage = lazyPage(
  () => import('./features/library/pages/DocumentPage'),
  'DocumentPage',
);
const SubmitPage = lazyPage(() => import('./features/library/pages/SubmitPage'), 'SubmitPage');
const MyDocumentsPage = lazyPage(
  () => import('./features/library/pages/MyDocumentsPage'),
  'MyDocumentsPage',
);
const ReviewQueuePage = lazyPage(
  () => import('./features/library/pages/manage/ReviewQueuePage'),
  'ReviewQueuePage',
);
const ReportsPage = lazyPage(
  () => import('./features/library/pages/manage/ReportsPage'),
  'ReportsPage',
);
const FoldersPage = lazyPage(
  () => import('./features/library/pages/manage/FoldersPage'),
  'FoldersPage',
);
const DirectUploadPage = lazyPage(
  () => import('./features/library/pages/manage/DirectUploadPage'),
  'DirectUploadPage',
);

const EventsCalendarPage = lazyPage(
  () => import('./features/event/student/EventsCalendarPage'),
  'EventsCalendarPage',
);
const EventManagerPage = lazyPage(
  () => import('./features/event/admin/EventManagerPage'),
  'EventManagerPage',
);
const CarpoolingMarketplacePage = lazyPage(
  () => import('./features/carpooling/student/CarpoolingMarketplacePage'),
  'CarpoolingMarketplacePage',
);
const MyRidesAndReliabilityPage = lazyPage(
  () => import('./features/carpooling/student/MyRidesAndReliabilityPage'),
  'MyRidesAndReliabilityPage',
);
const CarpoolingManagerPage = lazyPage(
  () => import('./features/carpooling/admin/CarpoolingManagerPage'),
  'CarpoolingManagerPage',
);

function AuthRoutes() {
  const { user, loading } = useAuth();
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-on-surface-variant">{t('common.loading')}</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <AppShell>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/*
          The dashboard was retired on 2026-09-21: nine centred cards that
          repeated the tab bar and carried no data. `/dashboard` stays as a
          redirect because it is in every bookmark, every old link and every
          test written before that date.
        */}
          <Route path="/" element={<Navigate to={HOME} replace />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/announcements" element={<AnnouncementsPage />} />
          <Route path="/announcements/:id" element={<AnnouncementDetailPage />} />
          <Route path="/complaints" element={<ComplaintsPage />} />
          <Route path="/complaints/new" element={<NewComplaintPage />} />
          <Route path="/complaints/:id" element={<ComplaintDetailPage />} />

          {/*
          RESTAURANT, RES-19 and RES-13.3. Every one of these is above the
          `/dashboard/:key` catch-all at the bottom of this table, which
          swallows anything declared below it.
        */}
          <Route path="/restaurant" element={<WeekMenuPage />} />
          <Route path="/restaurant/meals/:id" element={<MealDetailPage />} />
          <Route path="/restaurant/meals/:id/check-in" element={<CheckInPage />} />
          {/* RG156: every signed-in user, so no permission gate. */}
          <Route path="/restaurant/history" element={<DishHistoryPage />} />
          <Route path="/restaurant/history/:optionId" element={<DishDetailPage />} />

          {/*
          RES-20 and RES-12.4. Gated on a PERMISSION CODE, never a role name -
          uml/18 section 2.3. `RequirePermission` HIDES; the service checks
          `hasPermission` again inside itself, so a student who types the URL
          reaches the screen and then gets a 403 from every request it makes.
        */}
          <Route element={<RequirePermission code="MENU_MANAGE" />}>
            <Route path="/restaurant/manage" element={<MenusPage />} />
            <Route path="/restaurant/manage/menus/:menuId" element={<MenuBuilderPage />} />
          </Route>
          <Route element={<RequirePermission code="CHECKIN_MANAGE" />}>
            <Route path="/restaurant/manage/meals/:mealId/service" element={<MealServicePage />} />
          </Route>
          <Route element={<RequirePermission code="MEAL_PHOTO_UPLOAD" />}>
            <Route path="/restaurant/manage/meals/:mealId/photos" element={<MealPhotosPage />} />
          </Route>

          {/*
          LIBRARY, phase 7a. Every signed-in user; the API checks the session
          itself. Above the `/dashboard/:key` catch-all, which swallows
          `/dashboard/library` otherwise.
        */}
          <Route path="/library" element={<LibraryPage />} />
          <Route path="/library/folders/:folderId" element={<LibraryPage />} />
          <Route path="/library/documents/:id" element={<DocumentPage />} />
          {/* RG86, RG135: the manager publishes directly; the proposal form is for students. */}
          <Route
            path="/library/submit"
            element={
              <IfPermitted code="LIBRARY_MANAGE" fallback={<SubmitPage />}>
                <Navigate to="/dashboard/library/manage/upload" replace />
              </IfPermitted>
            }
          />
          <Route path="/library/mine" element={<MyDocumentsPage />} />

          {/*
          LIBRARY, phase 7b. Gated on a PERMISSION CODE, never a role name
          (uml/18 section 2.3). The gate hides; the API refuses anyway.
        */}
          <Route element={<RequirePermission code="LIBRARY_REVIEW" />}>
            <Route path="/library/review" element={<ReviewQueuePage />} />
          </Route>
          <Route element={<RequirePermission code="LIBRARY_MANAGE" />}>
            <Route path="/library/reports" element={<ReportsPage />} />
            <Route path="/library/manage/folders" element={<FoldersPage />} />
            <Route path="/library/manage/upload" element={<DirectUploadPage />} />
          </Route>

          {/* MyCalendar Routes (Unified Calendar & Events) */}
          <Route path="/calendar" element={<EventsCalendarPage />} />
          <Route path="/events" element={<Navigate to="/dashboard/calendar" replace />} />
          <Route
            path="/calendar/admin"
            element={
              <RequireRole allowedRoles={[UserRole.EVENT_MANAGER, UserRole.PRESIDENT]}>
                <EventManagerPage />
              </RequireRole>
            }
          />

          {/* MyCarpooling Routes (Marketplace, Driver & Passenger, Admin) */}
          <Route path="/carpooling" element={<CarpoolingMarketplacePage />} />
          <Route path="/carpooling/my-rides" element={<MyRidesAndReliabilityPage />} />
          <Route
            path="/carpooling/admin"
            element={
              <RequireRole allowedRoles={[UserRole.CARPOOLING_MANAGER, UserRole.PRESIDENT]}>
                <CarpoolingManagerPage />
              </RequireRole>
            }
          />

          <Route
            path="/announcements/new"
            element={
              <RequireRole allowedRoles={[UserRole.ANNOUNCEMENT_MANAGER, UserRole.PRESIDENT]}>
                <CreateAnnouncementPage />
              </RequireRole>
            }
          />
          <Route
            path="/announcements/:id/edit"
            element={
              <RequireRole allowedRoles={[UserRole.ANNOUNCEMENT_MANAGER, UserRole.PRESIDENT]}>
                <EditAnnouncementPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/users"
            element={
              <RequireRole allowedRoles={[UserRole.ADMIN, UserRole.PRESIDENT]}>
                <AdminUsersPage />
              </RequireRole>
            }
          />
          {/*
          The `/dashboard/:key` catch-all went with the dashboard. It served a
          "coming soon" page for module keys, and every module now has a real
          screen - so its only remaining job was answering a typo with a page
          that claimed a feature was unbuilt.

          This fallback MUST point at a real screen. It used to point at
          `/dashboard`, which no longer renders anything: a signed-in user with
          a stale URL would bounce between the two until the tab gave up.
        */}
          <Route path="*" element={<Navigate to={HOME} replace />} />
        </Routes>
      </Suspense>
    </AppShell>
  );
}

export default function App() {
  return (
    <QueryProvider>
      <AuthProvider>
        <ToastProvider>
          {/*
            DOM splat semantics, unchanged from v6: AuthRoutes' descendant
            <Routes> match against the REMAINDER of the /dashboard/* splat,
            so every inner path below is relative to /dashboard - never an
            absolute /dashboard/... string. The v7_relativeSplatPath future
            flag does not fix this in 6.30.x; absolute inner paths silently
            bounce every signed-in page HOME.
          */}
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignUpPage />} />
              <Route path="/verify" element={<VerifyPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              {/* Public legal documents. Before the /* auth splat: an explicit
                  path always outranks a splat in react-router, but the order
                  reads better here - public pages first, the gate last. */}
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              {/*
                P2. The gate now owns ONLY `/dashboard/*` - every screen a
                signed-in student can reach - and everything else that matches
                nothing is a real 404 instead of a silent bounce to /login. A
                typo is a typo, not a session that "ended". The signed-in
                splat *inside* AuthRoutes still redirects HOME (it answers a
                stale /dashboard/:key URL, never an unknown public path).
              */}
              <Route path="/dashboard/*" element={<AuthRoutes />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            <CookieBanner />
            <InstallAppBanner />
          </BrowserRouter>
        </ToastProvider>
      </AuthProvider>
    </QueryProvider>
  );
}
